import { Component } from '@angular/core';
import { Location } from '@angular/common';
import { Validators, FormGroup, FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { forkJoin } from 'rxjs';
import { CartaPorteService } from 'src/app/services/carta-porte.service';
import { OrderService } from 'src/app/services/order.service';
import { TrailerService } from 'src/app/services/trailer.service';
import { UtilsService } from 'src/app/services/utils.service';

declare const $: any;

@Component({
    selector: 'app-carta-porte-add',
    templateUrl: './carta-porte-add.component.html',
    styleUrls: ['./carta-porte-add.component.css']
})
export class CartaPorteAddComponent {
    private readonly destinationAddressFields = [
        'destination_calle',
        'destination_codigo_postal',
        'destination_pais',
        'destination_estado',
        'destination_municipio',
        'destination_localidad',
        'destination_colonia'
    ];
    private readonly originAddressFields = [
        'origin_calle',
        'origin_codigo_postal',
        'origin_pais',
        'origin_estado',
        'origin_municipio',
        'origin_localidad',
        'origin_colonia'
    ];
    private readonly addressFieldsToRemove = [
        ...this.originAddressFields,
        ...this.destinationAddressFields
    ];
    private readonly cfdiTaxesDefaults = {
        TotalImpuestosTrasladados: '',
        Traslados: [
            {
                Base: '',
                Impuesto: '002',
                TipoFactor: 'Tasa',
                TasaOCuota: '0.160000',
                Importe: '',
            }
        ]
    };
    private readonly defaultSubtotal = '';
    taxRatePercentDisplay = '16';
    private cfdiCalcInitialized = false;

    colonias: any[] = [];
    loadingColonias: boolean = false;
    dataDestination: any = {};
    coloniasOrigen: any[] = [];
    loadingColoniasOrigen: boolean = false;
    dataOrigin: any = {};
    existCodeOrigen: any = 0;

    constructor(
        private fb: FormBuilder,
        private utils: UtilsService,
        private spinner: NgxSpinnerService,
        private apiCartaPorte: CartaPorteService,
        private apiOrder: OrderService,
        private apiTrailer: TrailerService,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location
    ) { }

    selectedService: any = null;
    lockServiceSelect: boolean = false;
    selectedTrailer: any = null;

    services: any[] = [];
    transportMeans: any[] = [];
    weightUnits: any[] = [];
    logisticsTypes: any[] = [];
    measurementUnits: any[] = [];
    hazardousMaterialOptions: any[] = [];
    unitKeys: any[] = [];
    cargoInsurers: any[] = [];
    existCode: any = 0

    cartaPorteForm!: FormGroup;

    private loadDistanceForService(serviceId: number) {
        if (!serviceId) {
            return;
        }

        this.apiOrder.getDistanceRoute(serviceId).subscribe((resp: any) => {
            const km = resp?.total_km;
            if (km !== null && km !== undefined && km !== '') {
                this.cartaPorteForm.patchValue({ total_distance: Number(km) });
            }
        }, () => { });
    }

    private mapServiceForSelect(service: any) {
        if (!service) {
            return null;
        }

        return {
            id: service?.id,
            client: service?.client?.name ?? service?.client ?? '',
            unit: service?.unit?.unit ?? service?.unit ?? '',
            destination_address: service?.destination_address ?? null,
            origin_address: service?.origin_address ?? null,
            destination_components: service?.destination_components ? JSON.parse(service?.destination_components) : null
        };
    }

    private loadTrailerForSelectedService() {
        const requiresTrailer = !!(this.selectedService?.requires_trailer === true || this.selectedService?.requires_trailer === 1);
        const trailerId = Number(this.selectedService?.trailer_id);

        if (!requiresTrailer || !trailerId) {
            this.selectedTrailer = null;
            return;
        }

        this.apiTrailer.getId(trailerId).subscribe((resp: any) => {
            this.selectedTrailer = resp || null;
        }, () => {
            this.selectedTrailer = null;
        });
    }

    saveCfdiJson() {
        const serviceId = Number(this.cartaPorteForm.getRawValue()?.service_id);
        if (!serviceId) {
            this.utils.alertSwal({ title: 'Advertencia', text: 'Seleccione un servicio', icon: 'warning' });
            return;
        }

        this.spinner.show();
        this.apiCartaPorte.saveCfdiJson(serviceId).subscribe((resp: any) => {
            this.spinner.hide();
            this.utils.alertSwal({ title: 'Exitoso', text: resp?.msg || 'CFDI guardado', icon: 'success' });
        }, (err: any) => {
            this.spinner.hide();
            const msg = err?.error ? JSON.stringify(err.error) : 'Se ha presentado un error al guardar el CFDI';
            this.utils.alertSwal({ title: 'Error!', text: msg, icon: 'error' });
        });
    }

    saveAndStampCfdi() {
        if (!this.cartaPorteForm.valid) {
            this.utils.alertSwal({ title: 'Advertencia', text: 'Verifique que la información se encuentre completa.', icon: 'warning' });
            return;
        }

        const payload = this.buildPayload();
        const serviceId = Number(payload?.service_id);
        if (!serviceId) {
            this.utils.alertSwal({ title: 'Advertencia', text: 'Seleccione un servicio', icon: 'warning' });
            return;
        }

        this.spinner.show();
        this.apiCartaPorte.add(payload).subscribe(() => {
            this.apiCartaPorte.stampCfdi(serviceId).subscribe((resp: any) => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Exitoso', text: resp?.msg || 'Timbrado realizado', icon: 'success' });
            }, (err: any) => {
                this.spinner.hide();
                this.utils.alertSwal({
                    title: 'Advertencia',
                    text: this.extractErrorMessage(err, 'Timbrado no disponible'),
                    icon: 'warning'
                });
            });
        }, (err: any) => {
            this.spinner.hide();
            this.utils.alertSwal({
                title: 'Error!',
                text: this.extractErrorMessage(err, 'Se ha presentado un error al insertar carta porte antes del timbrado'),
                icon: 'error'
            });
        });
    }

    ngOnInit(): void {
        this.cartaPorteForm = this.fb.group({
            service_id: ['', Validators.required],
            transport_mean_id: ['', Validators.required],
            total_distance: ['', Validators.required],
            gross_weight_total: ['', Validators.required],
            weight_unit_id: ['', Validators.required],
            total_goods: ['', Validators.required],
            logistics_type_id: ['', Validators.required],
            dimensions: ['', Validators.required],
            measurement_unit_id: ['', Validators.required],
            hazardous_material_option_id: ['', Validators.required],
            unit_key_id: ['', Validators.required],
            cargo_insurer_id: [''],
            cargo_insurance_policy_number: [''],
            insurance_premium_value: [''],
            origin_calle: [{ value: '', disabled: true }],
            origin_codigo_postal: ['', Validators.required],
            origin_pais: [{ value: '', disabled: true }],
            origin_estado: [{ value: '', disabled: true }],
            origin_municipio: [{ value: '', disabled: true }],
            origin_localidad: [{ value: '', disabled: true }],
            origin_colonia: ['', Validators.required],
            origin_address: [null],
            destination_calle: [{ value: '', disabled: true }],
            destination_codigo_postal: ['', Validators.required],
            destination_pais: [{ value: '', disabled: true }],
            destination_estado: [{ value: '', disabled: true }],
            destination_municipio: [{ value: '', disabled: true }],
            destination_localidad: [{ value: '', disabled: true }],
            destination_colonia: ['', Validators.required],
            destination_address: [null],
            cfdi_subtotal: ['', [Validators.required, Validators.min(0)]],
            cfdi_total: ['', Validators.required],
            cfdi_total_impuestos_trasladados: [this.cfdiTaxesDefaults.TotalImpuestosTrasladados, Validators.required],
            cfdi_traslado_base: [this.cfdiTaxesDefaults.Traslados[0].Base, Validators.required],
            cfdi_traslado_impuesto: [this.cfdiTaxesDefaults.Traslados[0].Impuesto, Validators.required],
            cfdi_traslado_tipo_factor: [this.cfdiTaxesDefaults.Traslados[0].TipoFactor, Validators.required],
            cfdi_traslado_tasa_cuota: [this.cfdiTaxesDefaults.Traslados[0].TasaOCuota, Validators.required],
            cfdi_traslado_importe: [this.cfdiTaxesDefaults.Traslados[0].Importe, Validators.required],
        });

        this.setupCfdiRecalculation();
        this.setupDestinationAddress();
        this.setupOriginAddress();
        this.setupPostalCodeListener();

        const routeServiceId = this.route.snapshot.paramMap.get('serviceId');
        const serviceId = routeServiceId ? Number(routeServiceId) : null;
        this.lockServiceSelect = !!serviceId;

        this.spinner.show();
        const requests: any = {
            transportMeans: this.apiCartaPorte.getTransportMeans(),
            weightUnits: this.apiCartaPorte.getWeightUnits(),
            logisticsTypes: this.apiCartaPorte.getLogisticsTypes(),
            measurementUnits: this.apiCartaPorte.getMeasurementUnits(),
            hazardousMaterialOptions: this.apiCartaPorte.getHazardousMaterialOptions(),
            unitKeys: this.apiCartaPorte.getUnitKeys(),
            cargoInsurers: this.apiCartaPorte.getCargoInsurers(),
        };

        if (serviceId) {
            requests.serviceDetail = this.apiOrder.getId(serviceId);
        } else {
            requests.services = this.apiOrder.listService();
        }

        forkJoin(requests).subscribe((resp: any) => {
            if (serviceId) {
                this.selectedService = resp?.serviceDetail || null;
                const mapped = this.mapServiceForSelect(this.selectedService);
                this.services = mapped ? [mapped] : [];

                this.cartaPorteForm.patchValue({ 
                    service_id: serviceId, 
                    origin_calle: this.services[0]?.origin_address,
                    origin_pais: 'MEX',
                    destination_calle: this.services[0]?.destination_address,
                    destination_pais: 'MEX'
                }, { emitEvent: false });
                try {
                    this.cartaPorteForm.get('service_id')?.disable({ emitEvent: false });
                } catch { }

                this.loadDistanceForService(serviceId);
                this.loadTrailerForSelectedService();
            } else {
                // service/list puede venir como array o {data: []}
                const serviceResp = resp?.services;
                this.services = Array.isArray(serviceResp) ? serviceResp : (serviceResp?.data || []);
            }

            this.transportMeans = resp.transportMeans || [];
            this.weightUnits = resp.weightUnits || [];
            this.logisticsTypes = resp.logisticsTypes || [];
            this.measurementUnits = resp.measurementUnits || [];
            this.hazardousMaterialOptions = resp.hazardousMaterialOptions || [];
            this.unitKeys = resp.unitKeys || [];
            this.cargoInsurers = resp.cargoInsurers || [];
            this.spinner.hide();
        }, () => {
            this.spinner.hide();
        });

        this.cartaPorteForm.get('service_id')?.valueChanges.subscribe((selected: any) => {
            const sid = Number(selected);
            if (!sid) {
                return;
            }
            this.loadDistanceForService(sid);

            if (!this.lockServiceSelect) {
                this.apiOrder.getId(sid).subscribe((serviceResp: any) => {
                    this.selectedService = serviceResp || null;
                    this.loadTrailerForSelectedService();
                }, () => {
                    this.selectedService = null;
                    this.selectedTrailer = null;
                });
            }
        });
    }

    addCartaPorte() {
        if (this.cartaPorteForm.valid) {
            this.spinner.show();
            const payload = this.buildPayload();
            this.apiCartaPorte.add(payload).subscribe((resp: any) => {
                this.spinner.hide();
                try { $("#formCartaPorte")[0].reset(); } catch { }
                this.cartaPorteForm.reset();
                this.utils.alertSwal({ title: 'Exitoso', text: resp.msg, icon: 'success' });
                this.router.navigateByUrl('/carta-porte/list');
            }, (err: any) => {
                this.spinner.hide();
                const msg = err?.error ? JSON.stringify(err.error) : 'Se ha presentado un error al insertar carta porte';
                this.utils.alertSwal({ title: 'Error!', text: msg, icon: 'error' });
            });
        } else {
            this.utils.alertSwal({ title: 'Advertencia', text: 'Verifique que la información se encuentre completa.', icon: 'warning' });
        }
    }

    get registerFormControl() {
        return this.cartaPorteForm.controls;
    }

    getSelectedColoniaCode(): string {
        const selectedColoniaValue = this.cartaPorteForm.get('destination_colonia')?.value;
        if (!selectedColoniaValue) {
            return 'MEX'; // Default value
        }
        
        // Find the colonia object by c_Colonia
        const selectedColonia = this.colonias.find(c => c.c_Colonia === selectedColoniaValue);
        return selectedColonia ? selectedColonia.c_Colonia : selectedColoniaValue;
    }

    // Método para manejar cambios en el input de subtotal
    onSubtotalChange(event: any): void {
        const value = event.target.value;

        // Permitir que el campo esté vacío
        if (value === '' || value === null) {
            this.cartaPorteForm.get('cfdi_subtotal')?.setValue('', { emitEvent: false });
        } else {
            // Validar que sea un número válido
            const numValue = Number(value);
            if (!isNaN(numValue) && numValue >= 0) {
                this.cartaPorteForm.get('cfdi_subtotal')?.setValue(numValue, { emitEvent: false });
            }
        }

        // Forzar recálculo de impuestos
        this.recalculateCfdiTaxes();
    }

    private buildPayload() {
        const raw = this.cartaPorteForm.getRawValue();
        const payload: any = { ...raw };
        
        // Debug: Log the destination_address being sent
        const numericFields = [
            'service_id',
            'transport_mean_id',
            'total_distance',
            'weight_unit_id',
            'logistics_type_id',
            'measurement_unit_id',
            'hazardous_material_option_id',
            'unit_key_id',
            'cargo_insurer_id',
        ];

        numericFields.forEach(field => {
            payload[field] = payload[field] !== '' && payload[field] !== null ? Number(payload[field]) : payload[field];
        });

        payload.cfdi_taxes = this.buildCfdiTaxesPayload(raw);
        payload.cfdi_subtotal = this.formatNumberString(raw?.cfdi_subtotal, 2) ?? this.defaultSubtotal;
        payload.cfdi_total = this.formatNumberString(raw?.cfdi_total, 2)
            ?? (Number(payload.cfdi_subtotal) + Number(this.cfdiTaxesDefaults.TotalImpuestosTrasladados)).toFixed(2);

        // Eliminar campos individuales de dirección del payload final, solo dejar origin_address y destination_address
        this.addressFieldsToRemove.forEach(field => {
            delete payload[field];
        });

        return payload;
    }

    private buildCfdiTaxesPayload(raw: any) {
        return {
            TotalImpuestosTrasladados: this.formatNumberString(raw?.cfdi_total_impuestos_trasladados, 2) ?? this.cfdiTaxesDefaults.TotalImpuestosTrasladados,
            Traslados: [
                {
                    Base: this.formatNumberString(raw?.cfdi_traslado_base, 2) ?? this.cfdiTaxesDefaults.Traslados[0].Base,
                    Impuesto: raw?.cfdi_traslado_impuesto ?? this.cfdiTaxesDefaults.Traslados[0].Impuesto,
                    TipoFactor: raw?.cfdi_traslado_tipo_factor ?? this.cfdiTaxesDefaults.Traslados[0].TipoFactor,
                    TasaOCuota: this.formatNumberString(raw?.cfdi_traslado_tasa_cuota, 6) ?? this.cfdiTaxesDefaults.Traslados[0].TasaOCuota,
                    Importe: this.formatNumberString(raw?.cfdi_traslado_importe, 2) ?? this.cfdiTaxesDefaults.Traslados[0].Importe,
                }
            ]
        };
    }

    private formatNumberString(value: any, decimals: number): string | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        const num = Number(value);
        if (!isNaN(num)) {
            return num.toFixed(decimals);
        }

        return String(value);
    }

    private extractErrorMessage(err: any, fallback: string): string {
        return err?.error?.error?.conectia_response?.message
            || err?.error?.conectia_response?.message
            || err?.error?.error?.message
            || err?.error?.message
            || (err?.error ? JSON.stringify(err.error) : null)
            || err?.message
            || fallback;
    }

    private setupCfdiRecalculation() {
        if (this.cfdiCalcInitialized) {
            return;
        }
        this.cfdiCalcInitialized = true;

        ['cfdi_subtotal', 'cfdi_traslado_tasa_cuota'].forEach(controlName => {
            this.cartaPorteForm.get(controlName)?.valueChanges.subscribe(() => {
                this.recalculateCfdiTaxes();
            });
        });

        this.recalculateCfdiTaxes();
    }

    private recalculateCfdiTaxes() {
        const subtotalControl = this.cartaPorteForm.get('cfdi_subtotal');
        let subtotal = Number(subtotalControl?.value);

        // Si el campo está vacío o es inválido, usar el valor por defecto
        if (!subtotalControl?.value || subtotalControl?.value === '' || isNaN(subtotal)) {
            subtotal = Number(this.defaultSubtotal);
        }
        subtotal = this.round(subtotal, 2);

        const rateControl = this.cartaPorteForm.get('cfdi_traslado_tasa_cuota');
        let rate = Number(rateControl?.value);
        if (isNaN(rate)) {
            rate = Number(this.cfdiTaxesDefaults.Traslados[0].TasaOCuota);
        }
        rate = this.round(rate, 6);

        const tax = this.round(subtotal * rate, 2);
        const total = this.round(subtotal + tax, 2);

        // Actualizar los valores del formulario
        this.cartaPorteForm.get('cfdi_traslado_base')?.setValue(subtotal.toFixed(2), { emitEvent: false });
        this.cartaPorteForm.get('cfdi_traslado_importe')?.setValue(tax.toFixed(2), { emitEvent: false });
        this.cartaPorteForm.get('cfdi_total_impuestos_trasladados')?.setValue(tax.toFixed(2), { emitEvent: false });

        // Dejar cfdi_total vacío si el subtotal está vacío, si no calcular el total
        if (!subtotalControl?.value || subtotalControl?.value === '') {
            this.cartaPorteForm.get('cfdi_total')?.setValue('', { emitEvent: false });
        } else {
            this.cartaPorteForm.get('cfdi_total')?.setValue(total.toFixed(2), { emitEvent: false });
        }
    }

    private patchIfChanged(controlName: string, nextValue: string) {
        const control = this.cartaPorteForm.get(controlName);
        if (control && control.value !== nextValue) {
            control.patchValue(nextValue, { emitEvent: false });
        }
    }

    private round(value: number, precision: number): number {
        const multiplier = Math.pow(10, precision);
        return Math.round((value + Number.EPSILON) * multiplier) / multiplier;
    }

    private setupDestinationAddress() {
        this.setupAddressListeners(this.destinationAddressFields, () => this.updateDestinationAddress());
    }

    private updateDestinationAddress() {
        // If we have Sepomex data, use that structure
        if (this.dataDestination && this.dataDestination.c_CodigoPostal) {
            const selectedColonia = this.colonias.find(c => c.c_Colonia === this.cartaPorteForm.get('destination_colonia')?.value);
            
            const sepomexData = {
                c_CodigoPostal: this.dataDestination.c_CodigoPostal,
                c_Estado: this.dataDestination.c_Estado,
                estadoDescripcion: this.dataDestination.estadoDescripcion,
                c_Municipio: this.dataDestination.c_Municipio,
                municipioDescripcion: this.dataDestination.municipioDescripcion,
                c_Localidad: this.dataDestination.c_Localidad,
                localidadDescripcion: this.dataDestination.localidadDescripcion,
                c_Colonia: selectedColonia?.c_Colonia || '',
                coloniaDescripcion: selectedColonia?.descripcion || ''
            };
            
            this.cartaPorteForm.get('destination_address')?.setValue(sepomexData, { emitEvent: false });
        } else {
            // Fallback to basic structure if no Sepomex data (use SAT format for consistency)
            const addressData = {
                c_CodigoPostal: this.cartaPorteForm.get('destination_codigo_postal')?.value || '',
                c_Estado: this.cartaPorteForm.get('destination_estado')?.value || '',
                estadoDescripcion: this.cartaPorteForm.get('destination_estado')?.value || '',
                c_Municipio: this.cartaPorteForm.get('destination_municipio')?.value || '',
                municipioDescripcion: this.cartaPorteForm.get('destination_municipio')?.value || '',
                c_Localidad: this.cartaPorteForm.get('destination_localidad')?.value || '',
                localidadDescripcion: this.cartaPorteForm.get('destination_localidad')?.value || '',
                c_Colonia: this.cartaPorteForm.get('destination_colonia')?.value || '',
                coloniaDescripcion: this.cartaPorteForm.get('destination_colonia')?.value || ''
            };

            // Solo actualizar si hay datos, si no dejar como null
            const hasData = Object.values(addressData).some(value => value && typeof value === 'string' && value.trim() !== '');
            this.cartaPorteForm.get('destination_address')?.setValue(hasData ? addressData : null, { emitEvent: false });
        }
    }

    private fetchColoniasByPostalCode(postalCode: string) {
        if (!postalCode || postalCode.length < 4) {
            this.colonias = [];
            return;
        }

        this.loadingColonias = true;
        // Convertir a string para asegurar que la API lo maneje correctamente
        this.apiCartaPorte.getColoniasByPostalCode(String(postalCode)).subscribe({
            next: (response: any) => {
                this.loadingColonias = false;
                
                if (response && !response.message) { // Success response
                    this.colonias = response.colonias || [];
                    
                    // Only set dataDestination if we have valid Sepomex data (not just postal code)
                    if (response.c_CodigoPostal && response.c_Estado) {
                        this.dataDestination = response;
                        this.existCode = 1;
                        
                        // Auto-populate address fields with Sepomex data
                        this.cartaPorteForm.patchValue({
                            destination_pais: 'MEX', // Default country for Mexican postal codes
                            destination_estado: response.estadoDescripcion || '',
                            destination_municipio: response.municipioDescripcion || '',
                            destination_localidad: response.localidadDescripcion || '',
                        }, { emitEvent: false });
                        
                        // Manually trigger updateDestinationAddress after setting dataDestination
                        setTimeout(() => {
                            this.updateDestinationAddress();
                        }, 0);
                        
                    } else {
                        this.dataDestination = null;
                        this.existCode = 1;
                    }

                    // Auto-select the only available colonia if there's only one
                    if (this.colonias.length === 1) {
                        this.cartaPorteForm.patchValue({
                            destination_colonia: this.colonias[0].c_Colonia
                        }, { emitEvent: false });
                    }
                } else {
                    this.existCode = 2
                    this.colonias = [];
                    this.dataDestination = {}
                    // Clear address fields if no data found
                    this.cartaPorteForm.patchValue({
                        destination_pais: 'MEX',
                        destination_estado: '',
                        destination_municipio: '',
                        destination_localidad: '',
                    }, { emitEvent: false });
                }
            },
            error: (error) => {
                this.existCode = 2
                this.loadingColonias = false;
                this.dataDestination = {}
                this.cartaPorteForm.patchValue({
                        destination_pais: 'MEX',
                        destination_estado: '',
                        destination_municipio: '',
                        destination_localidad: '',
                    }, { emitEvent: false });
                this.colonias = [];
            }
        });
    }

    private onPostalCodeChange() {
        const postalCode = this.cartaPorteForm.get('destination_codigo_postal')?.value;
        // Convertir a string para la búsqueda, pero manejar tanto strings como números
        const postalCodeStr = String(postalCode || '').trim();
        this.fetchColoniasByPostalCode(postalCodeStr);
        
        // Clear colonia field when postal code changes
        this.cartaPorteForm.get('destination_colonia')?.setValue('', { emitEvent: false });
    }

    private setupPostalCodeListener() {
        // Esperar a que el DOM esté completamente renderizado
        setTimeout(() => {
            // Escuchar evento blur (cuando el campo pierde el foco)
            const postalCodeElement = document.querySelector('input[formcontrolname="destination_codigo_postal"]');
            if (postalCodeElement) {
                postalCodeElement.addEventListener('blur', () => {
                    this.onPostalCodeChange();
                });
            }

            // Escuchar evento blur para código postal origen
            const postalCodeOrigenElement = document.querySelector('input[formcontrolname="origin_codigo_postal"]');
            if (postalCodeOrigenElement) {
                postalCodeOrigenElement.addEventListener('blur', () => {
                    this.onPostalCodeOrigenChange();
                });
            }
        }, 100); // Pequeño retraso para asegurar que el DOM esté listo
    }

    private setupOriginAddress() {
        this.setupAddressListeners(this.originAddressFields, () => this.updateOriginAddress());
    }

    private updateOriginAddress() {
        // If we have Sepomex data, use that structure
        if (this.dataOrigin && this.dataOrigin.c_CodigoPostal) {
            const selectedColonia = this.coloniasOrigen.find(c => c.c_Colonia === this.cartaPorteForm.get('origin_colonia')?.value);
            
            const sepomexData = {
                c_CodigoPostal: this.dataOrigin.c_CodigoPostal,
                c_Estado: this.dataOrigin.c_Estado,
                estadoDescripcion: this.dataOrigin.estadoDescripcion,
                c_Municipio: this.dataOrigin.c_Municipio,
                municipioDescripcion: this.dataOrigin.municipioDescripcion,
                c_Localidad: this.dataOrigin.c_Localidad,
                localidadDescripcion: this.dataOrigin.localidadDescripcion,
                c_Colonia: selectedColonia?.c_Colonia || '',
                coloniaDescripcion: selectedColonia?.descripcion || ''
            };
            
            this.cartaPorteForm.get('origin_address')?.setValue(sepomexData, { emitEvent: false });
        } else {
            // Fallback to basic structure if no Sepomex data (use SAT format for consistency)
            const addressData = {
                c_CodigoPostal: this.cartaPorteForm.get('origin_codigo_postal')?.value || '',
                c_Estado: this.cartaPorteForm.get('origin_estado')?.value || '',
                estadoDescripcion: this.cartaPorteForm.get('origin_estado')?.value || '',
                c_Municipio: this.cartaPorteForm.get('origin_municipio')?.value || '',
                municipioDescripcion: this.cartaPorteForm.get('origin_municipio')?.value || '',
                c_Localidad: this.cartaPorteForm.get('origin_localidad')?.value || '',
                localidadDescripcion: this.cartaPorteForm.get('origin_localidad')?.value || '',
                c_Colonia: this.cartaPorteForm.get('origin_colonia')?.value || '',
                coloniaDescripcion: this.cartaPorteForm.get('origin_colonia')?.value || ''
            };

            // Solo actualizar si hay datos, si no dejar como null
            const hasData = Object.values(addressData).some(value => value && typeof value === 'string' && value.trim() !== '');
            this.cartaPorteForm.get('origin_address')?.setValue(hasData ? addressData : null, { emitEvent: false });
        }
    }

    private fetchColoniasByPostalCodeOrigen(postalCode: string) {
        if (!postalCode || postalCode.length < 4) {
            this.coloniasOrigen = [];
            return;
        }

        this.loadingColoniasOrigen = true;
        // Convertir a string para asegurar que la API lo maneje correctamente
        this.apiCartaPorte.getColoniasByPostalCode(String(postalCode)).subscribe({
            next: (response: any) => {
                this.loadingColoniasOrigen = false;
                
                if (response && !response.message) { // Success response
                    this.coloniasOrigen = response.colonias || [];
                    
                    // Only set dataOrigin if we have valid Sepomex data (not just postal code)
                    if (response.c_CodigoPostal && response.c_Estado) {
                        this.dataOrigin = response;
                        this.existCodeOrigen = 1;
                        
                        // Auto-populate address fields with Sepomex data
                        this.cartaPorteForm.patchValue({
                            origin_pais: 'MEX', // Default country for Mexican postal codes
                            origin_estado: response.estadoDescripcion || '',
                            origin_municipio: response.municipioDescripcion || '',
                            origin_localidad: response.localidadDescripcion || '',
                        }, { emitEvent: false });
                        
                        // Manually trigger updateOriginAddress after setting dataOrigin
                        setTimeout(() => {
                            this.updateOriginAddress();
                        }, 0);
                        
                    } else {
                        this.dataOrigin = null;
                        this.existCodeOrigen = 1;
                    }

                    // Auto-select the only available colonia if there's only one
                    if (this.coloniasOrigen.length === 1) {
                        this.cartaPorteForm.patchValue({
                            origin_colonia: this.coloniasOrigen[0].c_Colonia
                        }, { emitEvent: false });
                    }
                } else {
                    this.existCodeOrigen = 2
                    this.coloniasOrigen = [];
                    this.dataOrigin = {}
                    // Clear address fields if no data found
                    this.cartaPorteForm.patchValue({
                        origin_pais: 'MEX',
                        origin_estado: '',
                        origin_municipio: '',
                        origin_localidad: '',
                    }, { emitEvent: false });
                }
            },
            error: (error) => {
                this.existCodeOrigen = 2
                this.loadingColoniasOrigen = false;
                this.dataOrigin = {}
                this.cartaPorteForm.patchValue({
                        origin_pais: 'MEX',
                        origin_estado: '',
                        origin_municipio: '',
                        origin_localidad: '',
                    }, { emitEvent: false });
                this.coloniasOrigen = [];
            }
        });
    }

    private onPostalCodeOrigenChange() {
        const postalCode = this.cartaPorteForm.get('origin_codigo_postal')?.value;
        // Convertir a string para la búsqueda, pero manejar tanto strings como números
        const postalCodeStr = String(postalCode || '').trim();
        this.fetchColoniasByPostalCodeOrigen(postalCodeStr);
        
        // Clear colonia field when postal code changes
        this.cartaPorteForm.get('origin_colonia')?.setValue('', { emitEvent: false });
    }

    getSelectedColoniaOrigenCode(): string {
        const selectedColoniaValue = this.cartaPorteForm.get('origin_colonia')?.value;
        if (!selectedColoniaValue) {
            return 'MEX'; // Default value
        }
        
        // Find the colonia object by c_Colonia
        const selectedColonia = this.coloniasOrigen.find(c => c.c_Colonia === selectedColoniaValue);
        return selectedColonia ? selectedColonia.c_Colonia : selectedColoniaValue;
    }

    goBack() {
        this.location.back();
    }

    private setupAddressListeners(fields: string[], onChange: () => void) {
        fields.forEach(field => {
            this.cartaPorteForm.get(field)?.valueChanges.subscribe(() => onChange());
        });

        onChange();
    }
}
